import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { expenseService, eventService } from '../services/api';
import { divertilandiaColors } from '../theme/divertilandiaColors';

// Registrar Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  ChartTooltip,
  Legend
);

interface ReportData {
  period: string;
  income: number;
  expenses: number;
  profit: number;
}

interface SummaryData {
  totalIncome: number;
  totalExpenses: number;
  totalProfit: number;
  profitMargin: number;
}

interface ExpenseCategoryData {
  category: string;
  amount: number;
  color: string;
  percentage: number;
}

interface PackageSalesData {
  packageName: string;
  salesCount: number;
  totalRevenue: number;
  color: string;
  percentage: number;
}

interface TimeRange {
  start: Date;
  end: Date;
  label: string;
}

const Reports: React.FC = () => {
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [summaryData, setSummaryData] = useState<SummaryData | null>(null);
  const [expenseCategoryData, setExpenseCategoryData] = useState<ExpenseCategoryData[]>([]);
  const [expenseCategoryTimeData, setExpenseCategoryTimeData] = useState<any>(null);
  const [packageSalesData, setPackageSalesData] = useState<PackageSalesData[]>([]);
  const [packageTimeRange, setPackageTimeRange] = useState<TimeRange | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<'weekly' | 'monthly' | 'yearly'>('monthly');
  const [expenseTimeFilter, setExpenseTimeFilter] = useState<'weekly' | 'monthly' | 'yearly'>('monthly');
  const [packageTimeFilter, setPackageTimeFilter] = useState<'weekly' | 'monthly' | 'yearly'>('weekly');
  const [packageSortBy, setPackageSortBy] = useState<'sales' | 'revenue'>('sales');
  const [chartType, setChartType] = useState<'bar' | 'line'>('bar');

  useEffect(() => {
    fetchReportData();
  }, [timeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Recalcular gastos por categoría cuando cambie el filtro independiente
    if (expenseCategoryData.length > 0 || expenseTimeFilter !== timeFilter) {
      fetchExpenseCategoryData();
    }
  }, [expenseTimeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Recalcular paquetes más vendidos cuando cambie el filtro independiente
    if (packageSalesData.length > 0 || packageTimeFilter !== timeFilter) {
      fetchPackageSalesData();
    }
  }, [packageTimeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Reordenar datos cuando cambie el tipo de ordenamiento
    if (packageSalesData.length > 0) {
      const sortedData = [...packageSalesData].sort((a, b) => {
        if (packageSortBy === 'sales') {
          return b.salesCount - a.salesCount;
        } else {
          return b.totalRevenue - a.totalRevenue;
        }
      });
      setPackageSalesData(sortedData);
    }
  }, [packageSortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchReportData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Obtener datos de eventos (ingresos) y gastos
      const [events, expenses] = await Promise.all([
        eventService.getAll(),
        expenseService.getAll()
      ]);

      console.log('=== DEBUG REPORTES - DATOS RECIBIDOS ===');
      console.log('Total eventos:', events.length);
      console.log('Total gastos:', expenses.length);
      console.log('Primeros 3 eventos:', events.slice(0, 3));
      console.log('========================================');

      // Procesar datos según el filtro de tiempo
      const processedData = processDataByTimeFilter(events, expenses, timeFilter);
      setReportData(processedData);

      // Calcular resumen
      const summary = calculateSummary(processedData);
      setSummaryData(summary);

      // Calcular gastos por categoría con el filtro independiente
      const categoryData = calculateExpensesByCategory(expenses, expenseTimeFilter);
      setExpenseCategoryData(categoryData);

      // Calcular gastos por categoría a lo largo del tiempo para la gráfica de líneas
      const categoryTimeData = calculateExpensesByCategoryOverTime(expenses, expenseTimeFilter);
      setExpenseCategoryTimeData(categoryTimeData);

      // Calcular paquetes más vendidos
      const packageResult = calculatePackageSales(events, packageTimeFilter);
      setPackageSalesData(packageResult.data);
      setPackageTimeRange(packageResult.timeRange);

    } catch (err) {
      setError('Error al cargar los datos de reportes');
      console.error('Error fetching report data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenseCategoryData = async () => {
    try {
      // Obtener solo los gastos para el cálculo independiente
      const expenses = await expenseService.getAll();
      
      // Calcular gastos por categoría con el filtro independiente
      const categoryData = calculateExpensesByCategory(expenses, expenseTimeFilter);
      setExpenseCategoryData(categoryData);

      // Calcular gastos por categoría a lo largo del tiempo para la gráfica de líneas
      const categoryTimeData = calculateExpensesByCategoryOverTime(expenses, expenseTimeFilter);
      setExpenseCategoryTimeData(categoryTimeData);
    } catch (err) {
      console.error('Error fetching expense category data:', err);
    }
  };

  const fetchPackageSalesData = async () => {
    try {
      // Obtener solo los eventos para el cálculo independiente
      const events = await eventService.getAll();
      
      // Calcular paquetes más vendidos
      const packageResult = calculatePackageSales(events, packageTimeFilter);
      setPackageSalesData(packageResult.data);
      setPackageTimeRange(packageResult.timeRange);
    } catch (err) {
      console.error('Error fetching package sales data:', err);
    }
  };

  // Funciones auxiliares para manejo de semanas
  const getWeekNumber = (date: Date): number => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  };

  const getDateFromWeek = (year: number, week: number): Date => {
    const firstDayOfYear = new Date(year, 0, 1);
    const days = (week - 1) * 7 - firstDayOfYear.getDay() + 1;
    return new Date(year, 0, days);
  };

  const processDataByTimeFilter = (events: any[], expenses: any[], filter: string): ReportData[] => {
    const now = new Date();
    const data: ReportData[] = [];

    if (filter === 'weekly') {
      // Últimas 12 semanas del año actual
      const currentWeek = getWeekNumber(now);
      const startWeek = Math.max(1, currentWeek - 11);
      
      for (let week = startWeek; week <= currentWeek; week++) {
        const weekStart = getDateFromWeek(now.getFullYear(), week);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);

        const weekIncome = calculateIncomeForPeriod(events, weekStart, weekEnd);
        const weekExpenses = calculateExpensesForPeriod(expenses, weekStart, weekEnd);
        const weekProfit = weekIncome - weekExpenses;

        data.push({
          period: `Sem ${week}`,
          income: weekIncome,
          expenses: weekExpenses,
          profit: weekProfit,
        });
      }
    } else if (filter === 'monthly') {
      // Todo el año actual (enero a diciembre)
      for (let month = 0; month < 12; month++) {
        const monthStart = new Date(now.getFullYear(), month, 1);
        const monthEnd = new Date(now.getFullYear(), month + 1, 0);

        const monthIncome = calculateIncomeForPeriod(events, monthStart, monthEnd);
        const monthExpenses = calculateExpensesForPeriod(expenses, monthStart, monthEnd);
        const monthProfit = monthIncome - monthExpenses;

        data.push({
          period: monthStart.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' }),
          income: monthIncome,
          expenses: monthExpenses,
          profit: monthProfit,
        });
      }
    } else if (filter === 'yearly') {
      // Últimos 5 años
      for (let i = 4; i >= 0; i--) {
        const yearStart = new Date(now.getFullYear() - i, 0, 1);
        const yearEnd = new Date(now.getFullYear() - i, 11, 31);

        const yearIncome = calculateIncomeForPeriod(events, yearStart, yearEnd);
        const yearExpenses = calculateExpensesForPeriod(expenses, yearStart, yearEnd);
        const yearProfit = yearIncome - yearExpenses;

        data.push({
          period: yearStart.getFullYear().toString(),
          income: yearIncome,
          expenses: yearExpenses,
          profit: yearProfit,
        });
      }
    }

    return data;
  };

  const calculateIncomeForPeriod = (events: any[], startDate: Date, endDate: Date): number => {
    return events
      .filter(event => {
        const eventDate = new Date(event.event_date);
        // Incluir todos los eventos excepto cancelados
        return eventDate >= startDate && eventDate <= endDate && event.status !== 'cancelled';
      })
      .reduce((total, event) => {
        // Usar la misma lógica que en Calendar.tsx
        const packagePrice = Number(event.package?.price || 0);
        const discountPercentage = Number(event.discount_percentage || 0);
        
        // Calcular extras si no están disponibles
        let totalExtrasCost = Number(event.total_extras_cost || 0);
        if (totalExtrasCost === 0) {
          // Calcular extras manualmente si no están en la base de datos
          const extraHourCost = 100; // $100 por hora extra
          const tableSetCost = 100; // $100 por conjunto (mesa + 4 sillas)
          const playpenCost = 200; // $200 por corralito
          
          const hoursCount = Number(event.extra_hours_count) || 0;
          const tablesCount = Number(event.extra_tables) || 0;
          const playpensCount = Number(event.extra_playpens) || 0;
          const toysCount = Number(event.extra_toys) || 0;
          const servicesCost = Number(event.extra_services_cost) || 0;
          
          const hoursCost = hoursCount * extraHourCost;
          const tablesCost = tablesCount * tableSetCost;
          const playpensCost = playpensCount * playpenCost;
          const toysCost = toysCount * 100; // $100 por juguete extra
          
          totalExtrasCost = hoursCost + tablesCost + playpensCost + toysCost + servicesCost;
        }
        
        // Calcular precio total considerando descuento y extras
        const discountAmount = packagePrice * (discountPercentage / 100);
        const packagePriceWithDiscount = packagePrice - discountAmount;
        const totalCost = packagePriceWithDiscount + totalExtrasCost;
        
        console.log(`Reporte - Evento: ${event.contact_name}, Estado: ${event.status}, Paquete: $${packagePrice}, Descuento: ${discountPercentage}%, Extras: $${totalExtrasCost}, Total: $${totalCost}`);
        
        // Aplicar la misma lógica que en Calendar.tsx
        if (event.status === 'completed') {
          // Evento completado: todo el monto cuenta como completado
          return total + totalCost;
        } else if (event.status === 'cancelled') {
          // Los eventos cancelados no cuentan para ningún ingreso
          return total;
        } else if (event.status === 'confirmed') {
          // Evento confirmado: solo el anticipo cuenta como completado, el resto como pendiente
          const advancePayment = Number(event.advance_payment_amount || 0);
          return total + advancePayment; // Solo contar el anticipo como ingreso completado
        } else {
          // pending, in_progress: no cuentan como ingresos completados
          return total;
        }
      }, 0);
  };

  const calculateExpensesForPeriod = (expenses: any[], startDate: Date, endDate: Date): number => {
    return expenses
      .filter(expense => {
        const expenseDate = new Date(expense.expense_date);
        return expenseDate >= startDate && expenseDate <= endDate;
      })
      .reduce((total, expense) => {
        const amount = typeof expense.amount === 'string' ? parseFloat(expense.amount) || 0 : expense.amount || 0;
        return total + amount;
      }, 0);
  };

  const calculateSummary = (data: ReportData[]): SummaryData => {
    const totalIncome = data.reduce((sum, item) => sum + item.income, 0);
    const totalExpenses = data.reduce((sum, item) => sum + item.expenses, 0);
    const totalProfit = totalIncome - totalExpenses;
    const profitMargin = totalIncome > 0 ? (totalProfit / totalIncome) * 100 : 0;

    console.log('=== DEBUG SUMMARY ===');
    console.log('Total Income:', totalIncome);
    console.log('Total Expenses:', totalExpenses);
    console.log('Total Profit:', totalProfit);
    console.log('Profit Margin:', profitMargin);
    console.log('====================');

    return {
      totalIncome,
      totalExpenses,
      totalProfit,
      profitMargin,
    };
  };

  const calculateExpensesByCategory = (expenses: any[], timeFilter: string): ExpenseCategoryData[] => {
    const now = new Date();
    let filteredExpenses = expenses;

    // Filtrar gastos según el período seleccionado
    if (timeFilter === 'weekly') {
      const currentWeek = getWeekNumber(now);
      const startWeek = Math.max(1, currentWeek - 11);
      const weekStart = getDateFromWeek(now.getFullYear(), startWeek);
      const weekEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filteredExpenses = expenses.filter(expense => {
        const expenseDate = new Date(expense.expense_date);
        return expenseDate >= weekStart && expenseDate <= weekEnd;
      });
    } else if (timeFilter === 'monthly') {
      const yearStart = new Date(now.getFullYear(), 0, 1);
      const yearEnd = new Date(now.getFullYear(), 11, 31);
      
      filteredExpenses = expenses.filter(expense => {
        const expenseDate = new Date(expense.expense_date);
        return expenseDate >= yearStart && expenseDate <= yearEnd;
      });
    } else if (timeFilter === 'yearly') {
      const yearStart = new Date(now.getFullYear() - 4, 0, 1);
      const yearEnd = new Date(now.getFullYear(), 11, 31);
      
      filteredExpenses = expenses.filter(expense => {
        const expenseDate = new Date(expense.expense_date);
        return expenseDate >= yearStart && expenseDate <= yearEnd;
      });
    }

    // Agrupar gastos por categoría
    const categoryMap = new Map<string, number>();
    
    filteredExpenses.forEach(expense => {
      const categoryName = expense.category?.name || 'Sin categoría';
      const amount = typeof expense.amount === 'string' ? parseFloat(expense.amount) || 0 : expense.amount || 0;
      
      if (categoryMap.has(categoryName)) {
        categoryMap.set(categoryName, categoryMap.get(categoryName)! + amount);
      } else {
        categoryMap.set(categoryName, amount);
      }
    });

    // Calcular total para porcentajes
    const totalAmount = Array.from(categoryMap.values()).reduce((sum, amount) => sum + amount, 0);

    // Colores predefinidos para las categorías
    const colors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
      '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384',
      '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'
    ];

    // Convertir a array y calcular porcentajes
    const result: ExpenseCategoryData[] = Array.from(categoryMap.entries()).map(([category, amount], index) => ({
      category,
      amount,
      color: colors[index % colors.length],
      percentage: totalAmount > 0 ? (amount / totalAmount) * 100 : 0
    }));

    // Ordenar por cantidad (mayor a menor)
    return result.sort((a, b) => b.amount - a.amount);
  };

  // Nueva función para calcular gastos por categoría por período (para gráfica de líneas)
  const calculateExpensesByCategoryOverTime = (expenses: any[], timeFilter: string): any => {
    const now = new Date();
    const data: any[] = [];
    const categoryTotals: { [key: string]: number[] } = {};

    if (timeFilter === 'weekly') {
      // Últimas 12 semanas del año actual
      const currentWeek = getWeekNumber(now);
      const startWeek = Math.max(1, currentWeek - 11);
      
      for (let week = startWeek; week <= currentWeek; week++) {
        const weekStart = getDateFromWeek(now.getFullYear(), week);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);

        const weekExpenses = expenses.filter(expense => {
          const expenseDate = new Date(expense.expense_date);
          return expenseDate >= weekStart && expenseDate <= weekEnd;
        });

        // Agrupar por categoría para esta semana
        const weekCategories: { [key: string]: number } = {};
        weekExpenses.forEach(expense => {
          const categoryName = expense.category?.name || 'Sin categoría';
          const amount = typeof expense.amount === 'string' ? parseFloat(expense.amount) || 0 : expense.amount || 0;
          weekCategories[categoryName] = (weekCategories[categoryName] || 0) + amount;
        });

        // Agregar datos para cada categoría
        Object.keys(weekCategories).forEach(category => {
          if (!categoryTotals[category]) {
            categoryTotals[category] = new Array(currentWeek - startWeek + 1).fill(0);
          }
          categoryTotals[category][week - startWeek] = weekCategories[category];
        });

        data.push(`Sem ${week}`);
      }
    } else if (timeFilter === 'monthly') {
      // Todo el año actual (enero a diciembre)
      for (let month = 0; month < 12; month++) {
        const monthStart = new Date(now.getFullYear(), month, 1);
        const monthEnd = new Date(now.getFullYear(), month + 1, 0);

        const monthExpenses = expenses.filter(expense => {
          const expenseDate = new Date(expense.expense_date);
          return expenseDate >= monthStart && expenseDate <= monthEnd;
        });

        // Agrupar por categoría para este mes
        const monthCategories: { [key: string]: number } = {};
        monthExpenses.forEach(expense => {
          const categoryName = expense.category?.name || 'Sin categoría';
          const amount = typeof expense.amount === 'string' ? parseFloat(expense.amount) || 0 : expense.amount || 0;
          monthCategories[categoryName] = (monthCategories[categoryName] || 0) + amount;
        });

        // Agregar datos para cada categoría
        Object.keys(monthCategories).forEach(category => {
          if (!categoryTotals[category]) {
            categoryTotals[category] = new Array(12).fill(0);
          }
          categoryTotals[category][month] = monthCategories[category];
        });

        data.push(monthStart.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' }));
      }
    } else if (timeFilter === 'yearly') {
      // Últimos 5 años
      for (let i = 4; i >= 0; i--) {
        const yearStart = new Date(now.getFullYear() - i, 0, 1);
        const yearEnd = new Date(now.getFullYear() - i, 11, 31);

        const yearExpenses = expenses.filter(expense => {
          const expenseDate = new Date(expense.expense_date);
          return expenseDate >= yearStart && expenseDate <= yearEnd;
        });

        // Agrupar por categoría para este año
        const yearCategories: { [key: string]: number } = {};
        yearExpenses.forEach(expense => {
          const categoryName = expense.category?.name || 'Sin categoría';
          const amount = typeof expense.amount === 'string' ? parseFloat(expense.amount) || 0 : expense.amount || 0;
          yearCategories[categoryName] = (yearCategories[categoryName] || 0) + amount;
        });

        // Agregar datos para cada categoría
        Object.keys(yearCategories).forEach(category => {
          if (!categoryTotals[category]) {
            categoryTotals[category] = new Array(5).fill(0);
          }
          categoryTotals[category][4 - i] = yearCategories[category];
        });

        data.push(yearStart.getFullYear().toString());
      }
    }

    return {
      labels: data,
      datasets: Object.keys(categoryTotals).map((category, index) => {
        const colors = [
          '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
          '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384',
          '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'
        ];
        return {
          label: category,
          data: categoryTotals[category],
          borderColor: colors[index % colors.length],
          backgroundColor: colors[index % colors.length],
          fill: false,
          tension: 0.1,
        };
      })
    };
  };

  // Función para calcular paquetes más vendidos
  const calculatePackageSales = (events: any[], timeFilter: string): { data: PackageSalesData[], timeRange: TimeRange } => {
    const now = new Date();
    let filteredEvents = events;
    let timeRange: TimeRange;

    // Filtrar eventos según el período seleccionado
    if (timeFilter === 'weekly') {
      const currentWeek = getWeekNumber(now);
      const startWeek = Math.max(1, currentWeek - 11);
      const weekStart = getDateFromWeek(now.getFullYear(), startWeek);
      const weekEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      timeRange = {
        start: weekStart,
        end: weekEnd,
        label: `Semana ${startWeek} - ${currentWeek} de ${now.getFullYear()}`
      };
      
      filteredEvents = events.filter(event => {
        const eventDate = new Date(event.event_date);
        return eventDate >= weekStart && eventDate <= weekEnd;
      });
    } else if (timeFilter === 'monthly') {
      const yearStart = new Date(now.getFullYear(), 0, 1);
      const yearEnd = new Date(now.getFullYear(), 11, 31);
      
      timeRange = {
        start: yearStart,
        end: yearEnd,
        label: `Enero - Diciembre ${now.getFullYear()}`
      };
      
      filteredEvents = events.filter(event => {
        const eventDate = new Date(event.event_date);
        return eventDate >= yearStart && eventDate <= yearEnd;
      });
    } else if (timeFilter === 'yearly') {
      const yearStart = new Date(now.getFullYear() - 4, 0, 1);
      const yearEnd = new Date(now.getFullYear(), 11, 31);
      
      timeRange = {
        start: yearStart,
        end: yearEnd,
        label: `${now.getFullYear() - 4} - ${now.getFullYear()}`
      };
      
      filteredEvents = events.filter(event => {
        const eventDate = new Date(event.event_date);
        return eventDate >= yearStart && eventDate <= yearEnd;
      });
    } else {
      timeRange = {
        start: new Date(),
        end: new Date(),
        label: 'Período no definido'
      };
    }

    // Agrupar eventos por paquete
    const packageMap = new Map<string, { count: number; revenue: number }>();
    
    filteredEvents.forEach(event => {
      // Solo contar eventos no cancelados
      if (event.status === 'cancelled') return;
      
      const packageName = event.package?.name || 'Sin paquete';
      const packagePrice = Number(event.package?.price || 0);
      const discountPercentage = Number(event.discount_percentage || 0);
      
      // Calcular precio con descuento
      const discountAmount = packagePrice * (discountPercentage / 100);
      const finalPrice = packagePrice - discountAmount;
      
      if (packageMap.has(packageName)) {
        const current = packageMap.get(packageName)!;
        packageMap.set(packageName, {
          count: current.count + 1,
          revenue: current.revenue + finalPrice
        });
      } else {
        packageMap.set(packageName, {
          count: 1,
          revenue: finalPrice
        });
      }
    });

    // Calcular total para porcentajes
    const totalSales = Array.from(packageMap.values()).reduce((sum, data) => sum + data.count, 0);

    // Colores predefinidos para los paquetes
    const colors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
      '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384',
      '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'
    ];

    // Convertir a array y calcular porcentajes
    const result: PackageSalesData[] = Array.from(packageMap.entries()).map(([packageName, data], index) => ({
      packageName,
      salesCount: data.count,
      totalRevenue: data.revenue,
      color: colors[index % colors.length],
      percentage: totalSales > 0 ? (data.count / totalSales) * 100 : 0
    }));

    // Ordenar por cantidad de ventas (mayor a menor) - el ordenamiento se hará en el useEffect
    const sortedResult = result.sort((a, b) => b.salesCount - a.salesCount);

    return {
      data: sortedResult,
      timeRange: timeRange
    };
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(amount);
  };

  // Configuración de las gráficas
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            return `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value: any) {
            return `$${value.toLocaleString()}`;
          }
        }
      }
    }
  };

  const chartData = {
    labels: reportData.map(item => item.period),
    datasets: [
      {
        label: 'Ingresos',
        data: reportData.map(item => item.income),
        backgroundColor: divertilandiaColors.rainbow.green,
        borderColor: divertilandiaColors.rainbow.green,
        borderWidth: 1,
      },
      {
        label: 'Gastos',
        data: reportData.map(item => item.expenses),
        backgroundColor: divertilandiaColors.rainbow.red,
        borderColor: divertilandiaColors.rainbow.red,
        borderWidth: 1,
      },
    ],
  };

  const profitChartData = {
    labels: reportData.map(item => item.period),
    datasets: [
      {
        label: 'Utilidad',
        data: reportData.map(item => item.profit),
        backgroundColor: reportData.map(item => 
          item.profit >= 0 ? divertilandiaColors.rainbow.green : divertilandiaColors.rainbow.red
        ),
        borderColor: reportData.map(item => 
          item.profit >= 0 ? divertilandiaColors.rainbow.green : divertilandiaColors.rainbow.red
        ),
        borderWidth: 1,
      },
    ],
  };


  const getTimeFilterLabel = (): string => {
    switch (timeFilter) {
      case 'weekly': return 'Semanales';
      case 'monthly': return 'Mensuales';
      case 'yearly': return 'Anuales';
      default: return 'Mensuales';
    }
  };

  const getExpenseTimeFilterLabel = (): string => {
    switch (expenseTimeFilter) {
      case 'weekly': return 'Semanales';
      case 'monthly': return 'Mensuales';
      case 'yearly': return 'Anuales';
      default: return 'Mensuales';
    }
  };

  const getPackageTimeFilterLabel = (): string => {
    switch (packageTimeFilter) {
      case 'weekly': return 'Semanales';
      case 'monthly': return 'Mensuales';
      case 'yearly': return 'Anuales';
      default: return 'Mensuales';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <AssessmentIcon sx={{ mr: 2, color: divertilandiaColors.standard.primary, fontSize: 32 }} />
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: divertilandiaColors.standard.primary }}>
          Reportes Financieros
        </Typography>
      </Box>

      {/* Controles para gráficas principales */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Período</InputLabel>
                <Select
                  value={timeFilter}
                  label="Período"
                  onChange={(e) => setTimeFilter(e.target.value as 'weekly' | 'monthly' | 'yearly')}
                >
                  <MenuItem value="weekly">Semanal</MenuItem>
                  <MenuItem value="monthly">Mensual</MenuItem>
                  <MenuItem value="yearly">Anual</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Gráfica</InputLabel>
                <Select
                  value={chartType}
                  label="Tipo de Gráfica"
                  onChange={(e) => setChartType(e.target.value as 'bar' | 'line')}
                >
                  <MenuItem value="bar">Barras</MenuItem>
                  <MenuItem value="line">Líneas</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Resumen */}
      {summaryData && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              backgroundColor: 'white', 
              border: `2px solid ${divertilandiaColors.rainbow.green}`,
              '&:hover': { boxShadow: 3 }
            }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'black' }}>
                      Ingresos Totales
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'black' }}>
                      {formatCurrency(summaryData.totalIncome)}
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 40, color: divertilandiaColors.rainbow.green }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              backgroundColor: 'white', 
              border: `2px solid ${divertilandiaColors.rainbow.red}`,
              '&:hover': { boxShadow: 3 }
            }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'black' }}>
                      Gastos Totales
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'black' }}>
                      {formatCurrency(summaryData.totalExpenses)}
                    </Typography>
                  </Box>
                  <TrendingDownIcon sx={{ fontSize: 40, color: divertilandiaColors.rainbow.red }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              backgroundColor: 'white', 
              border: `2px solid ${summaryData.totalProfit >= 0 ? divertilandiaColors.rainbow.green : divertilandiaColors.rainbow.red}`,
              '&:hover': { boxShadow: 3 }
            }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'black' }}>
                      Utilidad Neta
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'black' }}>
                      {formatCurrency(summaryData.totalProfit)}
                    </Typography>
                  </Box>
                  <MoneyIcon sx={{ fontSize: 40, color: summaryData.totalProfit >= 0 ? divertilandiaColors.rainbow.green : divertilandiaColors.rainbow.red }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              backgroundColor: 'white', 
              border: `2px solid ${divertilandiaColors.standard.primary}`,
              '&:hover': { boxShadow: 3 }
            }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'black' }}>
                      Margen de Utilidad
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'black' }}>
                      {summaryData.profitMargin.toFixed(1)}%
                    </Typography>
                  </Box>
                  <AssessmentIcon sx={{ fontSize: 40, color: divertilandiaColors.standard.primary }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Gráfica Principal */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Comparativo Ingresos vs Gastos - {getTimeFilterLabel()}
          </Typography>
          
          <Box height={400}>
            {chartType === 'bar' ? (
              <Bar data={chartData} options={chartOptions} />
            ) : (
              <Line data={chartData} options={chartOptions} />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Gráfica de Utilidad */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Utilidad Neta por Período
          </Typography>
          
          <Box height={300}>
            <Bar data={profitChartData} options={chartOptions} />
          </Box>
        </CardContent>
      </Card>

      {/* Gráfica de Gastos por Categoría */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Gastos por Categoría - {getExpenseTimeFilterLabel()}
            </Typography>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Período</InputLabel>
              <Select
                value={expenseTimeFilter}
                label="Período"
                onChange={(e) => setExpenseTimeFilter(e.target.value as 'weekly' | 'monthly' | 'yearly')}
              >
                <MenuItem value="weekly">Semanal</MenuItem>
                <MenuItem value="monthly">Mensual</MenuItem>
                <MenuItem value="yearly">Anual</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          {expenseCategoryTimeData && expenseCategoryTimeData.datasets.length > 0 ? (
            <Box height={400}>
              <Line data={expenseCategoryTimeData} options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'top' as const,
                  },
                  title: {
                    display: false,
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context: any) {
                        return `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`;
                      }
                    }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: function(value: any) {
                        return `$${value.toLocaleString()}`;
                      }
                    }
                  }
                }
              }} />
            </Box>
          ) : (
            <Box 
              display="flex" 
              alignItems="center" 
              justifyContent="center" 
              height={200}
              sx={{ 
                backgroundColor: 'grey.50', 
                borderRadius: 2,
                border: '2px dashed',
                borderColor: 'grey.300'
              }}
            >
              <Box textAlign="center">
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No hay gastos registrados
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Los gastos aparecerán aquí cuando se registren
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Gráfica de Paquetes Más Vendidos */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Paquetes Más Vendidos - {getPackageTimeFilterLabel()}
              </Typography>
              {packageTimeRange && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {packageTimeRange.label}
                </Typography>
              )}
            </Box>
            <Box display="flex" gap={2}>
              <FormControl sx={{ minWidth: 160 }}>
                <InputLabel>Ordenar por</InputLabel>
                <Select
                  value={packageSortBy}
                  label="Ordenar por"
                  onChange={(e) => setPackageSortBy(e.target.value as 'sales' | 'revenue')}
                >
                  <MenuItem value="sales">Cantidad de Ventas</MenuItem>
                  <MenuItem value="revenue">Ingresos Generados</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Período</InputLabel>
                <Select
                  value={packageTimeFilter}
                  label="Período"
                  onChange={(e) => setPackageTimeFilter(e.target.value as 'weekly' | 'monthly' | 'yearly')}
                >
                  <MenuItem value="weekly">Semanal</MenuItem>
                  <MenuItem value="monthly">Mensual</MenuItem>
                  <MenuItem value="yearly">Anual</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
          
          {packageSalesData.length > 0 ? (
            <Box height={400}>
              <Bar data={{
                labels: packageSalesData.map(item => item.packageName),
                datasets: [
                  {
                    label: packageSortBy === 'sales' ? 'Ventas' : 'Ingresos',
                    data: packageSalesData.map(item => packageSortBy === 'sales' ? item.salesCount : item.totalRevenue),
                    backgroundColor: packageSalesData.map(item => item.color),
                    borderColor: packageSalesData.map(item => item.color),
                    borderWidth: 1,
                  },
                ],
              }} options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'top' as const,
                    display: false,
                  },
                  title: {
                    display: false,
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context: any) {
                        const dataIndex = context.dataIndex;
                        const packageData = packageSalesData[dataIndex];
                        return [
                          `Ventas: ${packageData.salesCount}`,
                          `Ingresos: ${formatCurrency(packageData.totalRevenue)}`,
                          `Porcentaje: ${packageData.percentage.toFixed(1)}%`
                        ];
                      }
                    }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: packageSortBy === 'sales' ? {
                      stepSize: 1,
                      callback: function(value: any) {
                        return Number.isInteger(value) ? value : '';
                      }
                    } : {
                      callback: function(value: any) {
                        return `$${value.toLocaleString()}`;
                      }
                    }
                  },
                  x: {
                    ticks: {
                      maxRotation: 45,
                      minRotation: 45
                    }
                  }
                }
              }} />
            </Box>
          ) : (
            <Box 
              display="flex" 
              alignItems="center" 
              justifyContent="center" 
              height={200}
              sx={{ 
                backgroundColor: 'grey.50', 
                borderRadius: 2,
                border: '2px dashed',
                borderColor: 'grey.300'
              }}
            >
              <Box textAlign="center">
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No hay ventas registradas
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Los paquetes más vendidos aparecerán aquí cuando se registren eventos
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Reports;
